import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import { Button } from 'react-bootstrap'
import { LinkContainer } from 'react-router-bootstrap'

import TableComponent from '../../../components/shared/TableComponent'

import routes from '../../../constants/routes'
import { DialogBox } from '../../../components/shared'
import { BrokerStateMapping } from './BrokerStateMapping'
import { SalesforceSyncModal } from './SalesforceSyncModal'
import { synchronizeWithSalesforceReset } from '../../actions'

const BrokerageList = ({ title = '', ...props }) => {
  const [showEditModal, setShowEditModal] = useState(false)
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [selectedBrokerage, setSelectedBrokerage] = useState(null)
  const [data, setData] = useState([])
  const options = {
    defaultSortName: 'name',
    defaultSortOrder: 'asc',
    pageStartIndex: 1,
    paginationSize: 3,
    sizePerPage: 50,
  }
  const [updateCompleted, setUpdateCompleted] = useState(false)

  useEffect(() => {
    props.brokerages && setData(props.brokerages)
  }, [props.brokerages])

  useEffect(() => {
    const totals = document.querySelectorAll('.pagination-total')

    function ancestor(el, cls) {
      while ((el = el.parentElement) && !el.classList.contains(cls));
      return el
    }

    // workaround for IE!
    Array.prototype.forEach.call(totals, (el) => {
      const tableContainer = ancestor(el, 'react-bs-table-container'),
        toolbar = tableContainer.querySelector('.react-bs-table-tool-bar')

      if (toolbar) toolbar.appendChild(el)
    })
  }, [])

  const updateStateObject = (brokerId, authStates) => {
    var index = data.findIndex((x) => x.brokerId === brokerId)
    data[index].authStates = authStates
    setData(data)
    setUpdateCompleted(true)
  }

  const actionButtonsCellFormatter = (cell, row) => {
    return (
      <LinkContainer to={routes.BROKERAGE.replace(':brokerageId', row.brokerId)}>
        <Button className="column-btn btn-default">Manage Brokers</Button>
      </LinkContainer>
    )
  }

  const popupButtonsCellFormatter = (handler, buttonLabel, setDisable) => (cell, row) => {
    return (
      <div className="updateColumn">
        <Button
          disabled={typeof setDisable === 'function' ? setDisable(row) : false}
          onClick={() => {
            return handler(row)
          }}
        >
          {buttonLabel}
        </Button>
      </div>
    )
  }

  const territoryCellFormatter = (cell, row) => {
    const location = [row.city, row.state].filter(Boolean).join(', ')
    if (!location) return cell || ''
    if (!cell) return location
    return `${cell} (${location})`
  }

  const statusCellFormatter = (cell, row) => {
    if (row.status === 'disabled') {
      return row.status
    }
    return 'active'
  }

  const handleEditUser = (row) => {
    setShowEditModal(true)
    setSelectedBrokerage(row)
  }

  const hideEditModal = () => {
    setShowEditModal(false)
    setSelectedBrokerage(null)
    setUpdateCompleted(false)
  }

  /**
   * The handleAgencyShowSyncModal is used for syncing data for a
   * specific Agency. So verify and agencyCode exists for the row before
   * displaying the SalesforceSync modal.
   */
  const handleAgencyShowSyncModal = (row) => {
    if (typeof row.agencyCode === 'string' && row.agencyCode.length > 0) {
      props.synchronizeWithSalesforceReset()
      setShowSyncModal(true)
      setSelectedBrokerage(row)
    }
  }

  /**
   * The handleShowSyncModal is used for syncing all recent
   * Agency data so no need to check for a specific agencyCode.
   */
  const handleShowSyncModal = () => {
    props.synchronizeWithSalesforceReset()
    setShowSyncModal(true)
  }

  const handleHideSyncModal = () => {
    props.synchronizeWithSalesforceReset()
    setShowSyncModal(false)
    setSelectedBrokerage(null)
  }

  const generateBrokeragesTableColumns = () => {
    return [
      { dataField: 'brokerId', isKey: true, width: '0%', hidden: true },
      { dataField: 'name', title: 'Name', width: '32%', isSortable: true },
      { dataField: 'agencyCode', title: 'Agency Code', width: '8%', isSortable: true },
      { dataField: 'commission', title: 'Commission', width: '8%', isSortable: true },
      { dataField: 'state', title: 'State', width: '6%', isSortable: true },
      { dataField: 'territory', dataFormat: territoryCellFormatter, title: 'Territory', width: '10%', isSortable: true },
      { dataField: 'status', dataFormat: statusCellFormatter, title: 'Status', width: '6%', isSortable: true },
      {
        dataFormat: popupButtonsCellFormatter(
          handleEditUser,
          'State Mapping',
          (row) => row.status === 'disabled'
        ),
        title: 'State Mapping',
        width: '10%',
        dataAlign: 'center',
      },
      { dataFormat: actionButtonsCellFormatter, width: '12%', dataAlign: 'center' },
      {
        dataFormat: popupButtonsCellFormatter(handleAgencyShowSyncModal, 'Sync'),
        title: 'Salesforce',
        width: '8%',
        dataAlign: 'center',
      },
    ]
  }

  return (
    <div>
      <TableComponent
        title={title}
        options={options}
        data={data}
        customizedColumn
        columns={generateBrokeragesTableColumns()}
        isBrokeragesTable={true}
      />
      <div className="salesforceSyncButtonContainer">
        <Button
          onClick={() => handleShowSyncModal()}
        >
          Salesforce Synchronization
        </Button>
      </div>
      <DialogBox
        title="Project State Mapping"
        custom_class="State Mapping"
        show={showEditModal}
        onHide={hideEditModal}
      >
        <BrokerStateMapping
          hideEditModal={hideEditModal}
          authAgency={selectedBrokerage}
          updateStateObject={updateStateObject}
          updateCompleted={updateCompleted}
        />
      </DialogBox>
      <DialogBox
        title="Salesforce Synchronization"
        show={showSyncModal}
        onHide={handleHideSyncModal}
      >
        <SalesforceSyncModal
          selectedAgency={selectedBrokerage}
        />
      </DialogBox>
    </div>
  )
}

BrokerageList.propTypes = {
  brokerages: PropTypes.array.isRequired,
  title: PropTypes.string
}

const connectedBrokerageList = connect(
  null,
  { synchronizeWithSalesforceReset })(BrokerageList)

export { connectedBrokerageList as BrokerageList }
